import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { MessageView } from "@/interfaces/Chat";
import { compareChatRevision } from "./useChatSync";

/** Only this container scrolls; loaded messages are never assumed to have been read. */
export const useChatScroll = ({
  conversationId,
  messages,
  onOlder,
  onVisibleMessage,
}: {
  conversationId: string | null;
  messages: MessageView[];
  onOlder: () => unknown;
  onVisibleMessage: (id: string) => void;
}) => {
  const historyRef = useRef<HTMLDivElement | null>(null);
  const callbacks = useRef({ onOlder, onVisibleMessage });
  callbacks.current = { onOlder, onVisibleMessage };
  const previous = useRef({ conversationId, first: "", last: "", height: 0 });
  const atBottom = useRef(true);
  const loadingOlder = useRef(false);
  const pendingTarget = useRef<string | null>(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const scrollToLatest = useCallback(() => {
    const node = historyRef.current;
    if (node) node.scrollTop = node.scrollHeight;
    atBottom.current = true;
    pendingTarget.current = null;
    setHasNewMessages(false);
  }, []);
  const scrollToMessage = useCallback((id: string) => {
    pendingTarget.current = id;
    const node = historyRef.current;
    const row =
      node &&
      Array.from(node.querySelectorAll<HTMLElement>("[data-message-id]")).find(
        (element) => element.dataset.messageId === id,
      );
    if (node && row) {
      node.scrollTop +=
        row.getBoundingClientRect().top - node.getBoundingClientRect().top - 24;
      row.focus({ preventScroll: true });
      pendingTarget.current = null;
      atBottom.current = false;
      return true;
    }
    return false;
  }, []);
  useLayoutEffect(() => {
    const node = historyRef.current;
    if (!node) return;
    const first = messages[0]?.id ?? "",
      last = messages[messages.length - 1]?.id ?? "";
    const old = previous.current;
    if (old.conversationId !== conversationId) {
      atBottom.current = true;
      setHasNewMessages(false);
    }
    if (pendingTarget.current) scrollToMessage(pendingTarget.current);
    else if (!old.first || old.conversationId !== conversationId)
      scrollToLatest();
    else if (first !== old.first && messages.some((m) => m.id === old.first))
      node.scrollTop += node.scrollHeight - old.height;
    else if (last !== old.last) {
      if (atBottom.current) scrollToLatest();
      else setHasNewMessages(true);
    }
    previous.current = {
      conversationId,
      first,
      last,
      height: node.scrollHeight,
    };
  }, [conversationId, messages, scrollToLatest, scrollToMessage]);
  useEffect(() => {
    const node = historyRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (atBottom.current) node.scrollTop = node.scrollHeight;
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [conversationId, messages]);
  const onScroll = useCallback(() => {
    const node = historyRef.current;
    if (!node) return;
    atBottom.current =
      node.scrollHeight - node.clientHeight - node.scrollTop < 48;
    if (atBottom.current) setHasNewMessages(false);
    if (node.scrollTop < 40 && !loadingOlder.current) {
      loadingOlder.current = true;
      Promise.resolve(callbacks.current.onOlder()).finally(() => {
        loadingOlder.current = false;
      });
    }
  }, []);
  useEffect(() => {
    const node = historyRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (document.hidden || node.clientHeight <= 0) return;
        const visible = entries
          .filter(
            (entry) =>
              entry.isIntersecting &&
              entry.intersectionRatio >= 1 &&
              node.contains(entry.target),
          )
          .map((entry) =>
            messages.find(
              (m) => m.id === (entry.target as HTMLElement).dataset.chatReadId,
            ),
          )
          .filter((m): m is MessageView => !!m && m.kind !== "system")
          .sort((a, b) => compareChatRevision(a.sequence, b.sequence));
        if (visible.length)
          callbacks.current.onVisibleMessage(visible[visible.length - 1].id);
      },
      { root: node, threshold: 1 },
    );
    node
      .querySelectorAll("[data-chat-read-id]")
      .forEach((row) => observer.observe(row));
    return () => observer.disconnect();
  }, [messages, conversationId]);
  return {
    historyRef,
    onScroll,
    scrollToLatest,
    scrollToMessage,
    hasNewMessages,
  };
};
